import { getZombieInstanceFromPrimaryKey, isEdge, isNonNaNNumber, Milliseconds, type DatastoreObjectType, type EdgeInstanceType } from '@iotv/iotv-v3-types'
import { DEBUG_OVERRIDE, NOT_LAMBDA } from '../../../util/lambda/debug'
import type { IoTVEntitySequentialProgrammeDef } from '../EntitySequentialProgrammes/types'
import type { RollingPatternConfig } from '../EntityRollingProgrammes/types'
import dayjs from 'dayjs'
import type { DeviceConfig } from '../../DeviceControl'
import { isOnDemandProgrammeConstructorProps, OnDemandProgramme } from './OnDemandProgramme'

const debug = DEBUG_OVERRIDE || (NOT_LAMBDA && false)

/** CRA 2025 05 — TODO: this is bad technical debt, but I don't know how else to do it... */
export const commonSupportedTypes = ['WaterCannon'].sort((a, b) => a.localeCompare(b))

export const supportedTypes = Object.fromEntries(
  [...commonSupportedTypes]
    .filter((x) => x)
    .map((type) => [type, type])
    .sort(),
)

export type OnDemandProgrammeDef = IoTVEntitySequentialProgrammeDef

export type OnDemandProgrammeToItemEdgeTypeAttributes = {
  window: number
}

export type OnDemandProgrammeToItemEdgeInstance = EdgeInstanceType<string, OnDemandProgrammeToItemEdgeTypeAttributes> & {
  edgeAttributes: OnDemandProgrammeToItemEdgeTypeAttributes
}

export const isOnDemandProgrammeToItemEdgeType = (x: any): x is OnDemandProgrammeToItemEdgeInstance => {
  const e = x as OnDemandProgrammeToItemEdgeInstance
  const passes = !!e && isEdge(e) && !!e.edgeAttributes && isNonNaNNumber(e.edgeAttributes.window)

  debug && console.log(`INFO: isOnDemandProgrammeToItemEdgeType passes ${passes}`, x)
  return passes
}

export const isOnDemandShouldDisableReport = (requestedRollingConfig: RollingPatternConfig, programme: OnDemandProgramme): boolean => {
  if (requestedRollingConfig.startTimeUtcSeconds === 0) return false
  if (programme.startTime === 0 && requestedRollingConfig.startTimeUtcSeconds !== 0) return true

  const now = dayjs.utc().unix()
  const lastActiveTime = dayjs
    .utc(programme.startTime * Milliseconds.SECOND)
    .add(programme.getTotalRepeatTime(), 'minutes')
    .unix()

  debug && console.log('INFO: isOnDemandShouldDisableReport', now, programme.getTotalRepeatTime(), lastActiveTime)

  return lastActiveTime <= now
}

export type OnDemandProgrammeConstructorProps = {
  /** ID of the programme, which will be created if not present */
  id?: string

  /** Type of the entity to be scheduled by linked sequence */
  supportedType: string

  /** Name of the programme */
  name: string

  /** List of times (in milliseconds) at which the programme set has been applieed */
  appliedTimestamps: number[] | null

  /** UTC start time (in seconds) for this programme */
  startTime: number | null

  /**
   * Number of start time windows to use
   *
   * This, combined with the `windowDuration`, will determine the overall cycle of the programme, the `delayMins` is calculated based on `windows * windowDuration - windowDuration`.
   * This, plus a future start time for each window, will yield consecutively operational programmes
   */
  windows: number | null

  /** Duration (in minutes) of a window */
  windowDuration: number | null

  /**
   * Duration (in minutes) of the gap between windows
   *
   * The need for this is apprent when there is only one window, which without this setting (and its fallback when zero) a single window programme is disabled because the
   * calculated `delayMins` is zero (the device behavior then is to conclude the programme is disabled, as zero `delayMins` implies continous operation)
   */
  windowGapDuration: number

  /** Number of times the sequence will repeat */
  repeats: number
}
